import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financeApi, Receivable } from '../../api/finance';
import { masterDataApi } from '../../api/masterData';
import { projectsApi } from '../../api/projects';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = { outstanding: 'Outstanding', partially_collected: 'Partially Collected', collected: 'Collected' };

/** Real, honest scoping: no upstream module produces receivables automatically yet (no Sales/pledge-tracking module exists) - these are entered directly, most commonly a donor pledge or grant instalment not yet received. */
export function ReceivablesPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const receivablesQuery = useQuery({ queryKey: ['finance-receivables'], queryFn: () => financeApi.listReceivables() });
  const donorsQuery = useQuery({ queryKey: ['master-donors'], queryFn: () => masterDataApi.donors(), enabled: showForm });
  const projectsQuery = useQuery({ queryKey: ['projects-list'], queryFn: () => projectsApi.list(), enabled: showForm });
  const grantsQuery = useQuery({ queryKey: ['finance-grants'], queryFn: () => financeApi.listGrants(), enabled: showForm });
  const fiscalYearsQuery = useQuery({ queryKey: ['finance-fiscal-years'], queryFn: () => financeApi.listFiscalYears() });

  const { register, handleSubmit, reset } = useForm<Record<string, string>>();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['finance-receivables'] });

  const createMutation = useMutation({
    mutationFn: (v: Record<string, string>) => financeApi.createReceivable({
      description: v.description, donorId: v.donorId ? Number(v.donorId) : undefined, projectId: v.projectId ? Number(v.projectId) : undefined,
      grantId: v.grantId ? Number(v.grantId) : undefined, amount: Number(v.amount), dueDate: v.dueDate || undefined,
    }),
    onSuccess: (result) => { showSuccess(`Receivable created - ${result.data.receivableReference}`); invalidate(); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create receivable.'),
  });

  const [collectingId, setCollectingId] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [collectionDate, setCollectionDate] = useState(new Date().toISOString().slice(0, 10));
  const [fiscalYearId, setFiscalYearId] = useState<number | null>(null);
  const fiscalYearDetailQuery = useQuery({ queryKey: ['finance-fiscal-year-detail', fiscalYearId], queryFn: () => financeApi.getFiscalYear(fiscalYearId!), enabled: !!fiscalYearId });
  const [periodId, setPeriodId] = useState<number | null>(null);

  const collectMutation = useMutation({
    mutationFn: () => financeApi.collectReceivable(collectingId!, { amount: Number(amount), collectionDate, fiscalYearId: fiscalYearId!, accountingPeriodId: periodId! }),
    onSuccess: (result) => { showSuccess(`Collection recorded - journal ${result.data.journalReference}, now ${result.data.status}.`); invalidate(); setCollectingId(null); setAmount(''); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record collection - check Finance Settings has the Bank and Receivable accounts configured.'),
  });

  const periods = fiscalYearDetailQuery.data?.periods ?? [];

  return (
    <div className="page">
      <div className="page-header">
        <h1>Accounts Receivable</h1>
        <button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'New Receivable'}</button>
      </div>
      <p className="muted">Amounts owed to Verdantrise - most commonly a donor pledge or grant instalment not yet received.</p>

      {showForm && (
        <section className="card">
          <h2>New Receivable</h2>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>Description<input {...register('description', { required: true })} /></label>
            <label>
              Donor (optional)
              <select {...register('donorId')}>
                <option value="">- none -</option>
                {donorsQuery.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </label>
            <label>
              Project (optional)
              <select {...register('projectId')}>
                <option value="">- none -</option>
                {projectsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
              </select>
            </label>
            <label>
              Grant (optional)
              <select {...register('grantId')}>
                <option value="">- none -</option>
                {grantsQuery.data?.data.map((g) => <option key={g.id} value={g.id}>{g.grant_reference} - {g.grant_name}</option>)}
              </select>
            </label>
            <label>Amount<input type="number" step="0.01" {...register('amount', { required: true })} /></label>
            <label>Due Date (optional)<input type="date" {...register('dueDate')} /></label>
            <div className="form-actions"><button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create'}</button></div>
          </form>
        </section>
      )}

      {collectingId !== null && (
        <section className="card">
          <h2>Record Collection</h2>
          <label>Amount<input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
          <label>Collection Date<input type="date" value={collectionDate} onChange={(e) => setCollectionDate(e.target.value)} /></label>
          <label>
            Fiscal Year
            <select onChange={(e) => setFiscalYearId(Number(e.target.value) || null)}>
              <option value="">- select -</option>
              {fiscalYearsQuery.data?.data.map((fy) => <option key={fy.id} value={fy.id}>{fy.year_label}</option>)}
            </select>
          </label>
          <label>
            Accounting Period
            <select onChange={(e) => setPeriodId(Number(e.target.value) || null)}>
              <option value="">- select -</option>
              {periods.map((p) => <option key={p.id} value={p.id}>{p.period_label}</option>)}
            </select>
          </label>
          <div className="form-actions">
            <button type="button" onClick={() => collectMutation.mutate()} disabled={collectMutation.isPending || !amount || !periodId}>{collectMutation.isPending ? 'Recording…' : 'Record Collection'}</button>
            <button type="button" className="link-button" onClick={() => setCollectingId(null)}>Cancel</button>
          </div>
        </section>
      )}

      <section className="card">
        {receivablesQuery.data && (
          <DataTable<Receivable>
            rows={receivablesQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage="No receivables yet."
            columns={[
              { header: 'Reference', render: (r) => r.receivable_reference },
              { header: 'Description', render: (r) => r.description },
              { header: 'Donor', render: (r) => r.donor_name ?? '-' },
              { header: 'Amount', render: (r) => Number(r.amount).toLocaleString() },
              { header: 'Collected', render: (r) => Number(r.amount_collected).toLocaleString() },
              { header: 'Outstanding', render: (r) => (Number(r.amount) - Number(r.amount_collected)).toLocaleString() },
              { header: 'Status', render: (r) => <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
              { header: '', render: (r) => r.status !== 'collected' && <button className="link-button" onClick={() => setCollectingId(r.id)}>Record Collection</button> },
            ]}
          />
        )}
      </section>
    </div>
  );
}
